import { useState } from 'react'
import { XIcon } from 'lucide-react'
import { Badge } from '../ui/badge'
import { Input } from '../ui/input'
import { uploadFile } from '../../lib/uploadFile'

/**
 * `attachments` field input — uploads each picked file through `POST
 * /files/single` (`uploadFile`) and holds the returned file ids as the
 * field's value, matching the backend's `attachments` schema (an array of
 * uuid strings). File names are tracked locally only for display; the
 * value itself is just the ids.
 */
export function AttachmentsInput({
  id,
  value,
  onChange,
}: {
  id: string
  value: string[]
  onChange: (value: string[]) => void
}) {
  const [namesById, setNamesById] = useState<Record<string, string>>({})
  const [status, setStatus] = useState<'idle' | 'uploading' | 'error'>('idle')

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    setStatus('uploading')
    try {
      const uploaded: string[] = []
      const names: Record<string, string> = {}
      for (const file of Array.from(files)) {
        const result = await uploadFile(file)
        uploaded.push(result.id)
        names[result.id] = file.name
      }
      setNamesById((prev) => ({ ...prev, ...names }))
      onChange([...value, ...uploaded.filter((fileId) => !value.includes(fileId))])
      setStatus('idle')
    } catch {
      setStatus('error')
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {value.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {value.map((fileId) => (
            <Badge key={fileId} variant="secondary" className="gap-1">
              {namesById[fileId] ?? fileId}
              <button
                type="button"
                onClick={() => onChange(value.filter((existing) => existing !== fileId))}
                aria-label={`Remove ${namesById[fileId] ?? fileId}`}
              >
                <XIcon className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      ) : null}
      <Input
        id={id}
        type="file"
        multiple
        disabled={status === 'uploading'}
        onChange={(event) => {
          void handleFiles(event.target.files)
          event.target.value = ''
        }}
      />
      {status === 'uploading' ? <p className="text-muted-foreground text-xs">Uploading…</p> : null}
      {status === 'error' ? <p className="text-destructive text-xs">Upload failed. Try again.</p> : null}
    </div>
  )
}
